import React, { useEffect, useRef, useMemo } from 'react';
import Plotly from 'plotly.js-dist';

interface DataPoint {
  timestamp: number;
  percentage: number;
}

interface RankChartProps {
  data: DataPoint[];
  width?: number;
  height?: number;
  title?: string;
  className?: string;
  timeRange?: { start: number; end: number } | null;
}

const RankChart: React.FC<RankChartProps> = ({ 
  data, 
  width = 800, 
  height = 300, 
  title = "Rank Chart",
  className = "",
  timeRange
}) => {
  const plotRef = useRef<HTMLDivElement>(null);

  // 색상 결정 함수
  const getChartColor = (percentage: number) => {
    if (percentage <= 2) {
      return '#1b4'; // 초록색
    } else if (percentage <= 10) {
      return '#ea9'; // 주황색
    } else {
      return '#F00'; // 빨간색
    }
  };

  // Plotly 데이터 생성
  const plotData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const color = getChartColor(data[data.length - 1]?.percentage || 0);

    return [
      {
        type: 'scatter',
        mode: 'lines+markers',
        x: data.map(d => new Date(d.timestamp)),
        y: data.map(d => Math.max(0.01, d.percentage)), // 로그 스케일을 위해 최소값 0.1 설정
        line: {
          color: color,
          width: 2
        },
        marker: {
          size: 8,
          color: color,
          symbol: 'circle'
        },
        name: 'Percentile Rank'
      }
    ];
  }, [data]);

  // Plotly 레이아웃 설정
  const layout = useMemo(() => ({
    xaxis: {
      title: "",
      type: "date",
      range: timeRange ? [
        new Date(timeRange.start),
        new Date(timeRange.end)
      ] : undefined,
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      showline: true,
      linecolor: '#6B7280',
      tickfont: {
        color: '#9CA3AF',
        size: 10
      }
    },
    yaxis: {
      title: "",
      type: "log",
      autorange: false,
      range: [Math.log10(100), Math.log10(0.01)], // 로그 스케일 범위: 0.1 ~ 100
      side: "right",
      tickformat: ".2f",
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      showline: true,
      linecolor: '#6B7280',
      tickfont: {
        color: '#9CA3AF',
        size: 10
      },
      dtick: 1, // 로그 스케일에서 적절한 틱 간격
      tickmode: 'auto'
    },
    hovermode: "closest",
    margin: {
      l: 10,
      r: 45,
      b: 50,
      t: 40
    },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    font: {
      color: '#FFFFFF'
    },
    title: {
      text: title,
      font: {
        size: 14,
        color: '#FFFFFF'
      },
      x: 0.5,
      y: 0.95
    }
  }), [title, timeRange]);

  // Plotly 설정
  const config = useMemo(() => ({
    displayModeBar: false,
    responsive: true,
    staticPlot: false
  }), []);

  // 차트 렌더링
  useEffect(() => {
    const currentPlotRef = plotRef.current;
    if (!currentPlotRef || !data || data.length === 0) return;

    Plotly.newPlot(currentPlotRef, plotData, layout, config);

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (currentPlotRef) {
        Plotly.purge(currentPlotRef);
      }
    };
  }, [plotData, layout, config, data]);

  // 크기 변경 시 차트 리사이즈
  useEffect(() => {
    if (plotRef.current && data && data.length > 0) {
      Plotly.relayout(plotRef.current, {
        width: width,
        height: height
      });
    }
  }, [width, height, data]);

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 rounded-lg ${className}`} style={{ width, height }}>
        <p className="text-gray-400 text-sm">데이터가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className={`${className}`} style={{ minHeight: height }}>
      <div 
        ref={plotRef}
        style={{ width: '100%', height: height }}
      />
    </div>
  );
};

export default RankChart; 